import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectDealersList, setInfoDealer } from '../../reducers/dealersListReducer';
import { PlaceResultType } from '../../types/placeResultLocation';
import { GeoJSONProperties } from '../../hooks/useDealershipList';
import { RedPin, BlackPin } from '../../constants/Images';
import yourLocationPin from '../../assets/images/yourLocationPin.svg';
import { RADIUS_SLIDER_STEPS_MAP } from '../../constants/radiusValues';
import { calculateDistance } from '../../utils/calculateDistance';
import { MARKET } from '../../constants/market';

const Map: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    allDealersList,
    radius,
    centerLocation,
    dealersWithinRadius,
    infoDealer,
    selectedDealersIds,
  } = useSelector(selectDealersList);

  const containerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const prevInfoDealerRef = useRef<GeoJSON.Feature<GeoJSON.Point, GeoJSONProperties> | null>(null);
  const [dealerMarkers, setDealerMarkers] = useState<google.maps.Marker[]>([]);
  const [centerMarker, setCenterMarker] = useState<google.maps.Marker | null>(null);

  useEffect(() => {
    const handleMarkerClick = (markerData: GeoJSON.Feature<GeoJSON.Point, GeoJSONProperties>) => {
      // Handle marker click event
      dispatch(setInfoDealer(markerData));
    };

    if (containerRef.current && centerLocation && radius) {
      const googleMapOptions: google.maps.MapOptions = {
        center: {
          lat: centerLocation?.geometry?.location?.lat as number,
          lng: centerLocation?.geometry?.location?.lng as number,
        },
        zoom: 9 + RADIUS_SLIDER_STEPS_MAP.findIndex((step) => step === radius),
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
      };

      const map = new google.maps.Map(containerRef.current, googleMapOptions);
      mapInstanceRef.current = map;

      // Add a marker for the center location
      // eslint-disable-next-line no-new
      const newCenterMarker = new google.maps.Marker({
        position: googleMapOptions.center,
        map,
        title: centerLocation.name,
        icon: {
          url: yourLocationPin,
          scaledSize: new google.maps.Size(23, 23),
        },
        zIndex: 100,
      });

      setCenterMarker(newCenterMarker);

      // Add a circle around the center location
      const circleOptions: google.maps.CircleOptions = {
        center: googleMapOptions.center,
        radius: (MARKET === 'CAN' ? radius : radius * 1.609) * 1000, // Convert radius to meters
        strokeColor: '#4C4C4C',
        strokeOpacity: 0.3,
        strokeWeight: 1,
        fillColor: '#4C4C4C',
        fillOpacity: 0.3,
      };

      circleRef.current = new google.maps.Circle(circleOptions);
      circleRef.current.setMap(map);

      const radiusIndex = RADIUS_SLIDER_STEPS_MAP.findIndex((step) => step === radius);

      // Calculate the zoom level based on the radius index
      const minZoom = 2; // You can adjust the minimum zoom level as needed
      const maxZoom = 9; // You can adjust the maximum zoom level as needed
      const zoomRange = maxZoom - minZoom;
      const zoomIncrement = zoomRange / (RADIUS_SLIDER_STEPS_MAP.length - 1);
      const zoomLevel = maxZoom - radiusIndex * zoomIncrement;

      map.setZoom(zoomLevel);

      // Add markers for dealer locations
      const newDealersMarkers = allDealersList.features.map(
        (dealer: GeoJSON.Feature<GeoJSON.Point, GeoJSONProperties>) => {
          const dealerLocation = dealer.properties.address;
          const dealerId = dealer.properties.id;
          const isDealerWithinRadius = dealersWithinRadius.find(
            (element) => element.properties.id === dealerId,
          );

          // Create the marker icon
          const markerIcon = {
            url: isDealerWithinRadius ? RedPin : BlackPin, // Path to your external SVG file
            scaledSize: new google.maps.Size(23, 34), // Set the size of the marker icon
          };

          // eslint-disable-next-line no-new
          const dealerMarker = new google.maps.Marker({
            position: {
              lat: dealerLocation.latitude,
              lng: dealerLocation.longitude,
            },
            map,
            title: dealer.properties?.name,
            label: {
              text: dealer.properties?.id,
              color: 'transparent',
            },
            icon: markerIcon,
          });

          // Add click event listener for dealer marker
          dealerMarker.addListener('click', () => {
            handleMarkerClick(dealer);
          });

          return dealerMarker;
        },
      );

      setDealerMarkers(newDealersMarkers);
    }
  }, [allDealersList.features, centerLocation, dealersWithinRadius, dispatch, radius]);

  useEffect(() => {
    const handleMarkerSize = (marker: google.maps.Marker | undefined, selected: boolean) => {
      const markerColor = (marker?.getIcon() as google.maps.Icon).url;
      if (selected) {
        marker?.setIcon({
          url: markerColor, // Path to your external SVG file
          scaledSize: new google.maps.Size(31, 46),
        });
      } else {
        marker?.setIcon({
          url: markerColor, // Path to your external SVG file
          scaledSize: new google.maps.Size(22, 33),
        });
      }
    };

    if (prevInfoDealerRef?.current !== infoDealer) {
      if (prevInfoDealerRef.current !== null && Object.keys(prevInfoDealerRef.current).length) {
        const selectedMarker = dealerMarkers.find((dealerMarker) => {
          const label = dealerMarker.getLabel() as google.maps.MarkerLabel;

          return label?.text === prevInfoDealerRef?.current?.properties.id;
        });
        handleMarkerSize(selectedMarker, false);
      }
      if (Object.keys(infoDealer).length) {
        const selectedMarker = dealerMarkers.find((dealerMarker) => {
          const label = dealerMarker.getLabel() as google.maps.MarkerLabel;

          return label?.text === infoDealer?.properties.id;
        });
        handleMarkerSize(selectedMarker, true);
      }
    }

    // Update the previous infoDealer value after the effect runs
    prevInfoDealerRef.current = infoDealer;
  }, [infoDealer, dealerMarkers]);

  const checkWithinRadius = useCallback(
    (position: google.maps.LatLng, centerLocationValue: PlaceResultType, radiusValue: number) => {
      const location = centerLocationValue?.geometry?.location;
      if (!location || location?.lat === undefined || location?.lng === undefined) {
        return false; // Or handle the case when the location is not available
      }

      const distance = calculateDistance(
        position.lat(),
        position.lng(),
        location.lat,
        location.lng,
      );
      return distance <= radiusValue;
    },
    [],
  );

  useEffect(() => {
    if (!selectedDealersIds.length) {
      // If there isn't any selected dealer iterate over the markers
      dealerMarkers.map((dealerMarker) => {
        const dealerMarkerPosition = dealerMarker.getPosition();
        const isDealerMarkerWithinRadius = checkWithinRadius(
          dealerMarkerPosition as google.maps.LatLng,
          centerLocation,
          MARKET === 'CAN' ? radius : radius * 1.609,
        );
        dealerMarker.setIcon({
          url: isDealerMarkerWithinRadius ? RedPin : BlackPin,
          scaledSize: new google.maps.Size(23, 34),
        });

        return null;
      });
    }
  }, [
    allDealersList,
    dealerMarkers,
    centerLocation,
    radius,
    checkWithinRadius,
    selectedDealersIds,
  ]);

  useEffect(() => {
    // If some dealers are selected then set their Markers "Red" and make the other markers "Black"
    if (selectedDealersIds.length) {
      dealerMarkers.map((dealerMarker) => {
        const dealerMarkerId = (dealerMarker.getLabel() as google.maps.MarkerLabel)?.text;
        if (selectedDealersIds.includes(dealerMarkerId)) {
          dealerMarker.setIcon({
            url: RedPin,
            scaledSize: new google.maps.Size(23, 34),
          });
        } else {
          dealerMarker.setIcon({
            url: BlackPin,
            scaledSize: new google.maps.Size(23, 34),
          });
        }
        return null;
      });
    }
  }, [dealerMarkers, selectedDealersIds]);

  return <div ref={containerRef} style={{ height: '100%' }} />;
};

export default Map;
